// Read-only view of the REAL collaborations managed in project 1 (the influencer
// management dashboard). They live in the `prado_collab` schema on the same DB;
// Pulse reads them under the signed-in user's RLS (managing-org members only).
// Editing happens in the managing app — Pulse links out to it.
import { supabase } from "@/lib/supabase";

export interface ManagedInfluencer {
  name: string | null;
  email: string | null;
  avatar_url: string | null;
  instagram_handle: string | null;
  tiktok_handle: string | null;
  youtube_handle: string | null;
}

export interface ManagedCollab {
  id: string;
  title: string;
  status: string;
  budget: number | null;
  updated_at: string;
  created_at: string;
  influencer: ManagedInfluencer | null;
}

/** Statuses that count as "done" (everything else is an open/active collab). */
const TERMINAL = new Set(["completed", "cancelled"]);

/** The Prado workspace in the managing app. Members of other workspaces (a
 *  personal test workspace) must not see those collaborations counted here —
 *  the same scope as the public.collaborations view (migration 0024). */
const PRADO_COLLAB_ORG = "9d7563ef-79ad-4425-916d-e5561037579b";

export async function getManagedCollaborations(): Promise<ManagedCollab[]> {
  const { data, error } = await supabase
    .schema("prado_collab")
    .from("collaborations")
    .select(
      "id, title, status, budget, updated_at, created_at, influencer:influencers(name, email, avatar_url, instagram_handle, tiktok_handle, youtube_handle)",
    )
    .eq("org_id", PRADO_COLLAB_ORG)
    .order("updated_at", { ascending: false });
  if (error) throw error;
  // PostgREST returns the to-one embed as an object (or array in some shapes).
  return (data ?? []).map((r: any) => ({
    ...r,
    influencer: Array.isArray(r.influencer) ? (r.influencer[0] ?? null) : (r.influencer ?? null),
  })) as ManagedCollab[];
}

export interface ManagedStats {
  total: number;
  active: number;
  completed: number;
  total_budget: number;
}

export function managedStats(rows: ManagedCollab[]): ManagedStats {
  return {
    total: rows.length,
    active: rows.filter((r) => !TERMINAL.has(r.status)).length,
    completed: rows.filter((r) => r.status === "completed").length,
    total_budget: rows.reduce((s, r) => s + (r.budget ?? 0), 0),
  };
}

/** Friendly Dutch label + badge tone per collaboration_status. */
export const MANAGED_STATUS: Record<string, { label: string; variant: "success" | "warning" | "info" | "secondary" | "error" | "outline" }> = {
  proposal_draft: { label: "Voorstel (concept)", variant: "outline" },
  proposal_sent: { label: "Voorstel verzonden", variant: "info" },
  negotiating: { label: "Onderhandeling", variant: "warning" },
  contract_draft: { label: "Contract (concept)", variant: "outline" },
  contract_sent: { label: "Contract verzonden", variant: "info" },
  contract_signed: { label: "Contract getekend", variant: "info" },
  payment_pending: { label: "Betaling open", variant: "warning" },
  content_pending: { label: "Content in productie", variant: "warning" },
  content_published: { label: "Gepubliceerd", variant: "success" },
  performance_pending: { label: "Performance", variant: "info" },
  completed: { label: "Afgerond", variant: "success" },
  cancelled: { label: "Geannuleerd", variant: "secondary" },
};

/** Deep-link into the managing app to view/edit a collaboration. */
export const managedCollabUrl = (id: string) => `/managing/collaborations/${id}`;
